import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard } from "../App";
import { EmailGuard } from "../App";
import { Oval } from "react-loader-spinner"; // Import loader
import { FaEdit, FaHome, FaFeatherAlt, FaChevronLeft } from 'react-icons/fa'; // Import icons
import { StandardURL } from "../App";

const UpdateRental = () => {
    const [description, setDescription] = useState("");
    const [houseNumber, setHouseNumber] = useState("");
    const [isFetching, setIsFetching] = useState(true); // State for initial data loading
    const [isSubmitting, setIsSubmitting] = useState(false); // State for form submission loading
    
    const token = localStorage.getItem("token");
    const rentalId = localStorage.getItem("selectedRentalId");
    const navigate = useNavigate();

    // 1. Fetch current rental details to pre-fill the form (Necessary UX improvement)
    useEffect(() => {
        if (!token || !rentalId) {
            toast.error("Missing Rental ID or authentication token.");
            setIsFetching(false);
            return;
        }

        // Assuming a GET endpoint consistent with previous components (e.g., /records/:id)
        // If the API supports GET on the PATCH endpoint, that would also work.
        axios
            .get(
                `${StandardURL}/iNyumba/admin/rentals/records/${rentalId}`, 
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            )
            .then((response) => {
                const { description, house_number } = response.data.response;
                setDescription(description || "");
                setHouseNumber(house_number || "");
            })
            .catch((error) => {
                toast.error("Failed to fetch current rental details.");
                console.error("Error fetching rental:", error);
            })
            .finally(() => {
                setIsFetching(false);
            });
    }, [token, rentalId]);

    // 2. Handle the update logic
    const handleUpdateRental = () => {
        if (!rentalId || !token) {
            toast.error("Rental ID or token not found. Cannot proceed with update.");
            return;
        }

        setIsSubmitting(true); // Start loading

        const updatedRentalData = {
            description: description,
            house_number: houseNumber,
        };

        axios
            .patch(
                `${StandardURL}/iNyumba/admin/rentals/${rentalId}`,
                updatedRentalData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            )
            .then((response) => {
                toast.success(response.data.response);
            })
            .catch((error) => {
                const errorMessage = error.response?.data?.response || "Error updating rental.";
                toast.error(errorMessage);
                console.error("Error updating rental:", error);
            })
            .finally(() => {
                setIsSubmitting(false); // Stop loading
                navigate(-1); // Go back one step (Original functionality)
            });
    };
    
    const handleGoBack = () => navigate(-1);

    if (isFetching) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-50">
                <Oval
                    height={50}
                    width={50}
                    color="#3b82f6"
                    secondaryColor="#93c5fd"
                    ariaLabel="loading-indicator"
                />
                <p className="ml-3 text-lg text-gray-700">Loading rental details...</p>
            </div>
        );
    }
    
    // Check if the necessary ID is missing after fetch attempt
    if (!rentalId) {
        return (
            <div className="text-center p-10 mt-10 bg-white shadow-lg mx-auto max-w-lg rounded-xl">
                <p className="text-xl font-semibold text-red-600">Rental ID Missing</p>
                <p className="text-gray-500 mt-2">Cannot load the update form without a selected Rental ID.</p>
                <button
                    onClick={handleGoBack}
                    className="mt-5 text-gray-600 hover:text-blue-600 transition duration-150 flex items-center justify-center mx-auto"
                >
                    <FaChevronLeft className="mr-1 w-4 h-4" /> Go Back
                </button>
            </div>
        );
    }

    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-50 flex justify-center p-4 sm:p-6 lg:p-8">
                    <div className="w-full max-w-lg bg-white p-8 rounded-xl shadow-2xl border border-gray-100">
                        
                        {/* Header and Back Button */}
                        <div className="mb-8 border-b pb-4 flex justify-between items-center">
                            <h1 className="text-3xl font-extrabold text-gray-900 flex items-center">
                                <FaEdit className="text-green-600 mr-3" /> Update Rental Unit
                            </h1>
                            <button
                                onClick={handleGoBack}
                                className="text-gray-600 hover:text-blue-600 transition duration-150 flex items-center text-sm font-medium"
                            >
                                <FaChevronLeft className="mr-1 w-4 h-4" /> Go Back
                            </button>
                        </div>

                        {/* Rental ID Context */}
                        <div className="bg-green-50 border-l-4 border-green-400 text-green-800 p-4 mb-6 rounded">
                            <p className="font-semibold">
                                Editing Rental Unit: <span className="font-extrabold">{rentalId}</span>
                            </p>
                        </div>
                        
                        {/* Form */}
                        <div className="space-y-6">
                            
                            {/* House Number */}
                            <div>
                                <label htmlFor="houseNumber" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                    <FaHome className="mr-2 text-green-500" /> House Number / Unit Name
                                </label>
                                <input
                                    type="text"
                                    id="houseNumber"
                                    value={houseNumber}
                                    onChange={(e) => setHouseNumber(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500 transition duration-150"
                                    required
                                    placeholder="e.g., A-101 or Unit 5"
                                />
                            </div>

                            {/* Description */}
                            <div>
                                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                    <FaFeatherAlt className="mr-2 text-green-500" /> Description
                                </label>
                                <textarea
                                    id="description"
                                    rows="3"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500 transition duration-150"
                                    required
                                    placeholder="e.g., One bedroom apartment with balcony"
                                />
                            </div>
                            
                            {/* Submit Button */}
                            <button
                                onClick={handleUpdateRental}
                                disabled={isSubmitting || !description || !houseNumber}
                                className={`w-full inline-flex items-center justify-center font-bold py-3 px-4 rounded-lg shadow-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 
                                    ${isSubmitting
                                        ? "bg-gray-500 cursor-not-allowed"
                                        : "bg-green-600 hover:bg-green-700 text-white focus:ring-green-500"
                                    } disabled:opacity-70`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <Oval
                                            height={20}
                                            width={20}
                                            color="white"
                                            visible={true}
                                            ariaLabel="loading-indicator"
                                            secondaryColor="#ffffff"
                                            strokeWidth={5}
                                        />
                                        <span className="ml-2">Saving Changes...</span>
                                    </>
                                ) : (
                                    "Update Rental Unit"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </AuthGuard>
        </EmailGuard>
    );
};

export default UpdateRental;